import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHero } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';

interface FormData {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
}

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState<FormData>({ name: '', email: '', company: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6 text-primary" />,
      title: t('Address', 'ที่อยู่'),
      value: t('Bangkok, Thailand', 'กรุงเทพมหานคร ประเทศไทย'),
    },
    {
      icon: <Mail className="w-6 h-6 text-primary" />,
      title: t('Email', 'อีเมล'),
      value: 'info@yaruwat.com',
    },
    {
      icon: <Phone className="w-6 h-6 text-primary" />,
      title: t('Phone (Thai Customer Service)', 'โทรศัพท์ (บริการลูกค้าไทย)'),
      value: '+66 2 xxx xxxx',
    },
    {
      icon: <Phone className="w-6 h-6 text-primary" />,
      title: t('Phone (Chinese Customer Service)', 'โทรศัพท์ (บริการลูกค้าจีน)'),
      value: '+86 xxx xxxx xxxx',
    },
  ];

  return (
    <Layout>
      <PageHero
        title="Contact Us"
        titleTh="ติดต่อเรา"
        subtitle="Get in touch with our team"
        subtitleTh="ติดต่อทีมของเรา"
        image="/images/hero_3.jpeg"
        small
      />

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-primary font-semibold text-sm uppercase tracking-widest">
                {t('Reach Us', 'ติดต่อเรา')}
              </span>
              <h2 className="text-3xl font-bold text-foreground mt-2 mb-6">
                {t("Let's Talk Business", 'มาคุยเรื่องธุรกิจกัน')}
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {t(
                  'Whether you\'re interested in our products, exploring a partnership, or simply want to learn more about Yaruwat, our team is ready to assist you. We serve clients in Thailand, China, and globally.',
                  'ไม่ว่าคุณจะสนใจสินค้าของเรา สำรวจความร่วมมือ หรือแค่ต้องการเรียนรู้เพิ่มเติมเกี่ยวกับยารุวาท ทีมของเราพร้อมให้ความช่วยเหลือ เราให้บริการลูกค้าในประเทศไทย จีน และทั่วโลก'
                )}
              </p>

              <div className="space-y-5">
                {contactInfo.map((info) => (
                  <div key={info.title} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{info.title}</div>
                      <div className="font-semibold text-foreground">{info.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Business Inquiry Types */}
              <div className="mt-10 bg-muted/30 rounded-2xl p-6">
                <h3 className="font-bold text-foreground mb-4">
                  {t('We Can Help With', 'เราสามารถช่วยด้าน')}
                </h3>
                <ul className="space-y-2">
                  {[
                    { en: 'Medical device procurement & distribution', th: 'การจัดซื้อและจัดจำหน่ายอุปกรณ์การแพทย์' },
                    { en: 'Coffee sourcing & export partnership', th: 'การจัดหากาแฟและความร่วมมือส่งออก' },
                    { en: 'Thai fruit export to China (GACC)', th: 'การส่งออกผลไม้ไทยสู่จีน (GACC)' },
                    { en: 'AI live streaming setup for Chinese market', th: 'การตั้งค่าไลฟ์สดด้วย AI สำหรับตลาดจีน' },
                  ].map((item) => (
                    <li key={item.en} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      {t(item.en, item.th)}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <CheckCircle2 className="w-20 h-20 text-primary mx-auto mb-6" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">
                    {t('Message Sent!', 'ส่งข้อความแล้ว!')}
                  </h3>
                  <p className="text-muted-foreground">
                    {t(
                      'Thank you for contacting us. Our team will get back to you within 1-2 business days.',
                      'ขอบคุณที่ติดต่อเรา ทีมของเราจะติดต่อกลับภายใน 1-2 วันทำการ'
                    )}
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', company: '', subject: '', message: '' }); }}
                    className="mt-6 text-primary font-semibold hover:underline"
                  >
                    {t('Send Another Message', 'ส่งข้อความอีกครั้ง')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-8 shadow-md border border-border/50 space-y-5">
                  <h3 className="text-xl font-bold text-card-foreground mb-6">
                    {t('Send Us a Message', 'ส่งข้อความถึงเรา')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {t('Full Name', 'ชื่อ-นามสกุล')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                        placeholder={t('Your full name', 'ชื่อเต็มของคุณ')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {t('Email Address', 'ที่อยู่อีเมล')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                        placeholder="email@company.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t('Company / Organization', 'บริษัท / องค์กร')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                      placeholder={t('Your company name', 'ชื่อบริษัทของคุณ')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t('Inquiry Type', 'ประเภทการสอบถาม')} *
                    </label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    >
                      <option value="">{t('Select inquiry type', 'เลือกประเภทการสอบถาม')}</option>
                      <option value="medical">{t('Medical Devices', 'อุปกรณ์การแพทย์')}</option>
                      <option value="coffee">{t('Coffee Products', 'ผลิตภัณฑ์กาแฟ')}</option>
                      <option value="fruits">{t('Thai Fruits Export', 'ส่งออกผลไม้ไทย')}</option>
                      <option value="ai-live">{t('AI Live Streaming', 'ไลฟ์สดด้วย AI')}</option>
                      <option value="partnership">{t('Partnership Inquiry', 'การสอบถามความร่วมมือ')}</option>
                      <option value="other">{t('Other', 'อื่นๆ')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t('Message', 'ข้อความ')} *
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
                      placeholder={t('Tell us about your needs...', 'บอกเราเกี่ยวกับความต้องการของคุณ...')}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-4 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('Send Message', 'ส่งข้อความ')}
                      </>
                    )}
                  </button>

                  <p className="text-xs text-muted-foreground text-center">
                    {t(
                      'By submitting, you agree to our privacy policy. We respond within 1-2 business days.',
                      'โดยการส่ง คุณยอมรับนโยบายความเป็นส่วนตัวของเรา เราตอบกลับภายใน 1-2 วันทำการ'
                    )}
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
